import { Box, Grid } from '@mui/material';
import { formatInTimeZone } from 'date-fns-tz';
import de from 'date-fns/locale/de';

import ToggleButton from './ToggleButton';
import { rootSx } from './styles';
import ThemeProvider from '@toolbox/style/theme/common/ThemeProvider/ThemeProvider';
import DayPickerV2 from '@toolbox/components/Timeslots/DayPicker/DayPickerV2';

import type { TimeSlotType } from '@widget/api/types';
import type { DayPickerV2Props } from '@toolbox/components/Timeslots/DayPicker/DayPickerV2';
import type { GridTypeMap, SxProps, Theme } from '@mui/material';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import type { FC, Dispatch, SetStateAction } from 'react';

export interface TimeslotsProps {
  /** Props for DayPicker component. */
  dayPickerProps: Omit<DayPickerV2Props, 'selectedDate'>;
  /** Value of selected date for DayPicker and Timeslots. */
  selectedDate: string | null;
  selectedTimeslot: TimeSlotType | null;
  /** Date change handler for DayPicker and Timeslots. */
  onChange: Dispatch<SetStateAction<TimeSlotType | null>>;
  /** Material UI Grid container spacing value. */
  timeslotsSpacing?: number;
  /** Material UI Grid container columns value. */
  timeslotsGridColumns?: GridTypeMap['props']['columns'];
  /** Material UI Grid item props for each timeslot. */
  gridItemProps?: Omit<OverridableComponent<GridTypeMap>, 'children'>;
  /** If `true`, the DayPicker will be disabled. */
  disabled?: boolean;
  /** Overriding styles for Timeslots wrapper. */
  wrapperSx?: SxProps<Theme>;
  timeSlots: TimeSlotType[];
  timezone: string;
  submitButtonRef?: React.RefObject<HTMLButtonElement>;
}

const Timeslots: FC<TimeslotsProps> = ({
  dayPickerProps,
  selectedDate,
  selectedTimeslot,
  timeslotsSpacing = 2,
  timeslotsGridColumns = { xs: 12, sm: 12, md: 10 },
  gridItemProps = {},
  onChange,
  disabled = false,
  wrapperSx,
  timeSlots,
  timezone,
  submitButtonRef,
}) => {
  const handleScrollToSubmitButton = () => {
    const btnElBottom = submitButtonRef?.current?.getBoundingClientRect().bottom;
    const windowBottom = window.scrollY + window.innerHeight;
    if (btnElBottom && btnElBottom > windowBottom) {
      submitButtonRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };
  return (
    <ThemeProvider>
      <Box sx={rootSx(disabled, wrapperSx)}>
        <Box sx={{ marginBottom: 1 }}>
          <DayPickerV2 {...dayPickerProps} selectedDate={selectedDate} />
        </Box>
        <Box>
          <Grid container spacing={timeslotsSpacing} columns={timeslotsGridColumns}>
            {timeSlots.map((timeslot) => (
              <Grid
                key={`${timeslot.isoDateTime}-${timeslot.email}`}
                item
                xs={4}
                sm={3}
                md={2}
                {...gridItemProps}
              >
                <ToggleButton
                  aria-label={`${selectedDate}: ${timeslot.isoDateTime}`}
                  selected={timeslot.isoDateTime === selectedTimeslot?.isoDateTime}
                  onCheck={() => {
                    onChange(timeslot);
                    handleScrollToSubmitButton();
                  }}
                  label={`${formatInTimeZone(timeslot.isoDateTime, timezone, 'HH:mm', {
                    locale: de,
                  })}`}
                  hideCheckbox
                  labelVariant="body1"
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Timeslots;
